import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { GraduationCap, Briefcase } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const TEACHING = [
  "Lycée Le Corbusier",
  "Université de Strasbourg",
  "CESI Strasbourg",
  "MJM Graphic Design",
  "École d'Architecture de Constantine",
];

const PRACTICE_AREAS = {
  fr: [
    "Architecture & réhabilitation",
    "Aménagement intérieur",
    "Logements collectifs & individuels",
    "Équipements publics",
    "Concours & appels d'offres",
    "Coordination BIM & technique",
  ],
  en: [
    "Architecture & rehabilitation",
    "Interior design",
    "Collective & individual housing",
    "Public buildings",
    "Competitions & open calls",
    "BIM & technical coordination",
  ],
};

export default function Practice() {
  const { lang } = useLang();
  const areas = PRACTICE_AREAS[lang];

  return (
    <section
      id="practice"
      data-testid="practice-section"
      className="py-24 md:py-36 bg-[#FAFAF7] border-t border-b border-[#E5E5E5]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-8 h-px bg-[#0A0A0A]" />
          <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase">
            {lang === "fr" ? "Pratique & Transmission" : "Practice & Teaching"} / 01·B
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="font-display font-light uppercase tracking-[-0.02em] leading-[0.95] text-[clamp(2.5rem,6.5vw,6.5rem)] max-w-[20ch] mb-16 md:mb-24"
        >
          {lang === "fr" ? (
            <>
              Concevoir<span className="text-[#5C5C5C]"> · </span>
              <span className="text-[#5C5C5C] font-extralight">coordonner</span>
              <span className="text-[#5C5C5C]"> · </span>
              <span className="font-normal">transmettre</span>
              <span className="text-[#D35400]">.</span>
            </>
          ) : (
            <>
              Design<span className="text-[#5C5C5C]"> · </span>
              <span className="text-[#5C5C5C] font-extralight">coordinate</span>
              <span className="text-[#5C5C5C]"> · </span>
              <span className="font-normal">teach</span>
              <span className="text-[#D35400]">.</span>
            </>
          )}
        </motion.h2>

        {/* Intro paragraph */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease, delay: 0.1 }}
          className="grid grid-cols-12 gap-8 md:gap-12 mb-20 md:mb-28"
        >
          <div className="col-span-12 md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">
              {lang === "fr" ? "Parcours" : "Background"}
            </div>
          </div>
          <div className="col-span-12 md:col-span-9 space-y-6 text-base md:text-lg font-light leading-relaxed text-[#0A0A0A] max-w-[68ch]">
            <p>
              {lang === "fr"
                ? "Architecte diplômé de l'École Nationale Supérieure d'Architecture de Strasbourg et de l'INSA Strasbourg, Karim Kerkoub développe une pratique transversale entre architecture, projet urbain, BIM, coordination technique et transmission des savoirs."
                : "Graduate of the École Nationale Supérieure d'Architecture de Strasbourg and INSA Strasbourg, Karim Kerkoub develops a transversal practice combining architecture, urban projects, BIM, technical coordination and knowledge transmission."}
            </p>
            <p>
              {lang === "fr"
                ? "Formé également à l'École d'Architecture de Constantine, il construit une approche ouverte entre la France, le Luxembourg et l'Algérie, en croisant culture architecturale, expertise technique et expérience de terrain."
                : "Trained as well at the Constantine School of Architecture, he builds an open approach across France, Luxembourg and Algeria, weaving together architectural culture, technical expertise and field experience."}
            </p>
          </div>
        </motion.div>

        {/* Two-column layout: Practice / Teaching */}
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          {/* Left — Pratique professionnelle */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="col-span-12 md:col-span-6 border-t-2 border-[#0A0A0A] pt-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <Briefcase size={18} className="text-[#D35400]" />
              <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
                {lang === "fr" ? "Pratique professionnelle" : "Professional practice"}
              </h3>
            </div>

            <p className="text-base font-light leading-relaxed text-[#0A0A0A] mb-10 max-w-[52ch]">
              {lang === "fr"
                ? "Son travail s'appuie sur une pratique concrète du projet — de la faisabilité à la conception, jusqu'au suivi et à la coordination technique."
                : "His work relies on a hands-on project practice — from feasibility and design to monitoring and technical coordination."}
            </p>

            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-5">
              {lang === "fr" ? "Champs d'intervention" : "Fields of intervention"}
            </div>
            <ul className="space-y-3">
              {areas.map((a, i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-4 border-b border-[#E5E5E5] pb-3 group"
                >
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] group-hover:text-[#D35400] transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base md:text-lg font-light">{a}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right — Enseignement / Transmission */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease, delay: 0.15 }}
            className="col-span-12 md:col-span-6 border-t-2 border-[#D35400] pt-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <GraduationCap size={18} className="text-[#D35400]" />
              <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
                {lang === "fr" ? "Enseignement & transmission" : "Teaching & transmission"}
              </h3>
            </div>

            <p className="text-base font-light leading-relaxed text-[#0A0A0A] mb-10 max-w-[52ch]">
              {lang === "fr"
                ? "En parallèle de son activité d'architecte, il enseigne et transmet son expérience auprès de plusieurs établissements."
                : "Alongside his architectural practice, he teaches and shares his experience across several institutions."}
            </p>

            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-5">
              {lang === "fr" ? "Établissements" : "Institutions"}
            </div>
            <ul className="space-y-3">
              {TEACHING.map((name, i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-4 border-b border-[#E5E5E5] pb-3 group"
                >
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] group-hover:text-[#D35400] transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base md:text-lg font-light">{name}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Closing statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease, delay: 0.2 }}
          className="mt-20 md:mt-28 border-t border-[#0A0A0A] pt-10 md:pt-12"
        >
          <p className="font-display text-xl md:text-2xl font-light tracking-tight leading-relaxed text-[#0A0A0A] max-w-[68ch]">
            {lang === "fr"
              ? "Cette double posture, entre pratique et transmission, nourrit une vision globale du métier — concevoir, coordonner, expliquer et accompagner les projets avec rigueur, pédagogie et sens architectural."
              : "This dual posture, between practice and teaching, nurtures a global vision of the profession — to design, coordinate, explain and support projects with rigor, pedagogy and architectural sense."}
            <span className="text-[#D35400]">_</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
